import { MEETING_MINUTES } from '@sorted/types'

/**
 * Adding the meeting to a calendar.
 *
 * Two destinations, because they cover the real split rather than every
 * vendor:
 *
 *  - GOOGLE: a URL to Google's own pre-filled event screen. No download, and
 *    the user lands somewhere that obviously worked. The common Android path.
 *  - .ics FILE: Apple Calendar, Outlook and everything else. On iOS this is
 *    the only option — Safari has no add-event URL scheme — and it is handled
 *    well there: the file opens Calendar's import sheet directly.
 *
 * The reason to ask at all: a bare .ics download on Android drops into the
 * notification shade as a file, so someone who just tapped "add to calendar"
 * sees no calendar and assumes nothing happened.
 *
 * No timezone arithmetic here, unlike the usual version of this code. `meetAt`
 * is an absolute instant from the server, so both destinations get a UTC
 * stamp and land at the same moment wherever the phone thinks it is.
 */

/** iCalendar UTC form: 20260907T090000Z. Google's URL takes the same shape. */
function stamp(d: Date): string {
  return `${d.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`
}

export interface CalendarEvent {
  /** "Coffee at Café Sperl" */
  title: string
  /** Absolute ISO instant. */
  meetAt: string
  location: string
  /** The meeting link, so the event carries a way back to it. */
  url: string
}

/**
 * Google Calendar's "add event" screen, pre-filled.
 *
 * `details` doubles as the description because the format has no dedicated
 * URL field — so the link back to the meeting goes there.
 */
export function googleCalendarUrl(ev: CalendarEvent): string {
  const start = new Date(ev.meetAt)
  const end = new Date(start.getTime() + MEETING_MINUTES * 60_000)

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ev.title,
    dates: `${stamp(start)}/${stamp(end)}`,
    location: ev.location,
    details: ev.url,
  })

  return `https://calendar.google.com/calendar/render?${params.toString()}`
}
